import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ProductoAttachmentApiService } from '../services/producto-attachment-api.service';
import { ProductAttachmentResponse, ProductAttachmentRequest } from '../models/producto-attachment.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ATTACHMENT_TYPE_LABELS, productoLabel } from '../models/producto-labels';

@Component({
  selector: 'app-producto-attachments',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './producto-attachments.component.html'
})
export class ProductoAttachmentsComponent implements OnInit, OnChanges {
  @Input() productId!: number;

  attachments: ProductAttachmentResponse[] = [];
  loading = false;
  error = '';

  // Form state
  showForm = false;
  form!: FormGroup;
  editingId: number | null = null;
  saving = false;

  // Filter
  filterType: string | undefined;

  // Incidente #27: subida real de documentos desde el PC. El archivo se sube al
  // storage (POST /api/files/upload) y se registra como adjunto del producto
  // (POST /api/products/{id}/attachments) con fileUrl = storageRef (id del archivo).
  uploadingDoc = false;
  docError = '';
  // Adjunto que se está reemplazando (se sube uno nuevo y se elimina el anterior).
  private replacingAttachment: ProductAttachmentResponse | null = null;

  private businessId!: number;

  constructor(
    private fb: FormBuilder,
    private productApi: ProductoAttachmentApiService,
    private authService: AuthService,
    private datetimeService: DatetimeService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    // Inc 58: businessId real del usuario; el `|| 1` provocaba 403 fuera del negocio 1.
    this.businessId = this.authService.getBusinessId() ?? 1;
    this.initForm();
    this.cargarAttachments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['productId'] && this.businessId) {
      this.cargarAttachments();
    }
  }

  // ============================================================
  // Incidente #27: subir documentos desde el PC → crea un adjunto del producto
  // ============================================================

  /** Extensiones permitidas por el backend (documentos + imágenes). */
  private readonly ALLOWED_EXT = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'webp'];

  private attachmentTypeFor(file: File): string {
    return file.type.startsWith('image/') ? 'image' : 'document';
  }

  onDocumentoSeleccionado(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) return;

    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!this.ALLOWED_EXT.includes(ext)) {
      const msg = 'Formato no soportado. Permitidos: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, WEBP.';
      this.docError = msg;
      this.toast.error({ title: 'Formato no soportado', message: msg });
      this.replacingAttachment = null;
      return;
    }

    this.uploadingDoc = true;
    this.docError = '';
    // 1) subir el archivo real al storage → fileId (storageRef)
    this.productApi.uploadDocument(this.productId, file).subscribe({
      next: (res) => {
        // 2) registrar el adjunto del producto con fileUrl = storageRef
        const request: ProductAttachmentRequest = {
          attachmentType: this.attachmentTypeFor(file),
          filename: file.name,
          fileUrl: String(res.fileId),
          fileSize: res.fileSize,
          mimeType: res.contentType,
          isPublic: false
        };
        this.productApi.createProductAttachment(this.productId, this.businessId, request).subscribe({
          next: () => {
            this.uploadingDoc = false;
            const reemplazado = this.replacingAttachment;
            this.replacingAttachment = null;
            if (reemplazado) {
              // 3) al reemplazar, elimina el adjunto anterior
              this.productApi.deleteProductAttachment(this.productId, reemplazado.id, this.businessId).subscribe({
                next: () => { this.cargarAttachments(); this.toast.updated('Documento'); },
                error: () => { this.cargarAttachments(); this.toast.success({ title: 'Documento subido', message: `Se subió "${file.name}".` }); }
              });
            } else {
              this.cargarAttachments();
              this.toast.success({ title: 'Documento subido', message: `Se subió "${file.name}" correctamente.` });
            }
          },
          error: (err) => {
            this.uploadingDoc = false;
            this.replacingAttachment = null;
            const msg = err.error?.message || 'No se pudo registrar el documento (formato no soportado).';
            this.docError = msg;
            this.toast.error({ title: 'Error al subir', message: msg });
          }
        });
      },
      error: (err) => {
        this.uploadingDoc = false;
        this.replacingAttachment = null;
        const msg = err.error?.message || 'No se pudo subir el documento (formato o tamaño no permitido).';
        this.docError = msg;
        this.toast.error({ title: 'Error al subir', message: msg });
      }
    });
  }

  /** True si el adjunto es un archivo subido al storage (fileUrl = id numérico). */
  esArchivoSubido(att: ProductAttachmentResponse): boolean {
    return /^\d+$/.test((att.fileUrl || '').trim());
  }

  descargarAdjunto(att: ProductAttachmentResponse): void {
    if (!this.esArchivoSubido(att)) {
      window.open(att.fileUrl, '_blank');
      return;
    }
    this.productApi.downloadDocument(Number(att.fileUrl)).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = att.filename || 'documento';
        a.click();
        URL.revokeObjectURL(url);
      },
      error: (err) => this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo descargar el documento' })
    });
  }

  /** Reemplaza un documento subido: dispara el selector; al elegir, sube el nuevo y borra el anterior. */
  reemplazarAdjunto(att: ProductAttachmentResponse, fileInput: HTMLInputElement): void {
    this.replacingAttachment = att;
    fileInput.click();
  }

  private initForm(): void {
    this.form = this.fb.group({
      attachmentType: ['image', Validators.required],
      fileUrl: ['', [Validators.required, Validators.maxLength(500)]],
      filename: ['', Validators.maxLength(255)],
      mimeType: ['', Validators.maxLength(100)],
      thumbnailUrl: ['', Validators.maxLength(500)],
      displayOrder: [0],
      description: [''],
      isPublic: [true]
    });
  }

  cargarAttachments(): void {
    this.loading = true;
    this.error = '';
    this.productApi.searchProductAttachment({ businessId: this.businessId, productId: this.productId, attachmentType: this.filterType }).subscribe({
      next: (response) => {
        this.attachments = response.content;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar adjuntos';
        this.loading = false;
      }
    });
  }

  abrirFormNuevo(): void {
    this.editingId = null;
    this.form.reset({
      attachmentType: 'image',
      fileUrl: '',
      filename: '',
      mimeType: '',
      thumbnailUrl: '',
      displayOrder: 0,
      description: '',
      isPublic: true
    });
    this.showForm = true;
  }

  abrirFormEditar(attachment: ProductAttachmentResponse): void {
    this.editingId = attachment.id;
    this.form.patchValue({
      attachmentType: attachment.attachmentType,
      fileUrl: attachment.fileUrl,
      filename: attachment.filename || '',
      mimeType: attachment.mimeType || '',
      thumbnailUrl: attachment.thumbnailUrl || '',
      displayOrder: attachment.displayOrder || 0,
      description: attachment.description || '',
      isPublic: attachment.isPublic ?? true
    });
    this.showForm = true;
  }

  cerrarForm(): void {
    this.showForm = false;
    this.editingId = null;
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = '';

    const v = this.form.value;
    const request: ProductAttachmentRequest = {
      attachmentType: v.attachmentType,
      fileUrl: v.fileUrl,
      filename: v.filename || undefined,
      mimeType: v.mimeType || undefined,
      thumbnailUrl: v.thumbnailUrl || undefined,
      displayOrder: v.displayOrder ?? 0,
      description: v.description || undefined,
      isPublic: v.isPublic
    };

    if (this.editingId !== null) {
      this.productApi.updateProductAttachment(this.productId, this.editingId, this.businessId, request).subscribe({
        next: () => {
          this.saving = false;
          this.cerrarForm();
          this.cargarAttachments();
          this.toast.updated('Adjunto');
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar adjunto';
          this.toast.error({ title: 'Error', message: err.error?.message || 'Error al actualizar adjunto' });
        }
      });
    } else {
      this.productApi.createProductAttachment(this.productId, this.businessId, request).subscribe({
        next: () => {
          this.saving = false;
          this.cerrarForm();
          this.cargarAttachments();
          this.toast.created('Adjunto');
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear adjunto';
          this.toast.error({ title: 'Error', message: err.error?.message || 'Error al crear adjunto' });
        }
      });
    }
  }

  async eliminar(attachment: ProductAttachmentResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar adjunto',
      message: `¿Eliminar el adjunto "${attachment.filename || attachment.fileUrl}"?`
    });
    if (!ok) return;
    this.productApi.deleteProductAttachment(this.productId, attachment.id, this.businessId).subscribe({
      next: () => {
        this.cargarAttachments();
        this.toast.deleted('Adjunto');
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al eliminar adjunto';
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar adjunto' });
      }
    });
  }

  cambiarFiltro(): void {
    this.cargarAttachments();
  }

  getTypeLabel(type: string): string {
    return productoLabel(ATTACHMENT_TYPE_LABELS, type);
  }

  getTypeColor(type: string): string {
    const colors: Record<string, string> = {
      document: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      video: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      image: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      other: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
    };
    return colors[type] || colors['other'];
  }

  formatSize(bytes?: number): string {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    return this.datetimeService.formatDateTimeShort(dateString);
  }
}
